import { Object3D } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { Text } from 'troika-three-text'
import { disposeCadText, scheduleCadTextSync } from '@/plugins/cad/diagnostics/cadTextSync'
import type { MLeaderEntity } from '@/plugins/cad/module/CADModule/model/entity'
import {
  getMLeaderBranchPoints,
  getMLeaderTextPosition,
} from '@/plugins/cad/module/CADModule/model/entity'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import type { LineTypeMaterial } from '@/plugins/cad/module/CADModule/object/material/MaterialManager'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import { createCadText } from '@/plugins/cad/module/CADModule/object/text/CadTextFactory'
import { DrawUtils } from '@/plugins/cad/module/utils'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'

/** 多重引线渲染对象 */
export class MLeaderObject extends Object3D {
  private lineObject: Line2
  private textObject: Text
  /** 是否使用缓存材质，true 时销毁对象不释放材质 */
  public isCacheMaterial: boolean

  constructor(
    public entity: MLeaderEntity,
    material?: LineTypeMaterial,
    color?: number
  ) {
    super()
    this.isCacheMaterial = !!material
    this.lineObject = this.createLineObject(material)
    this.textObject = this.createTextObject(color)
    this.init()
    this.onUpdateGeometry()
  }

  /** 当前引线段使用的线型材质 */
  get material() {
    return this.lineObject.material as LineTypeMaterial
  }

  private get materialColor() {
    return this.entity.color
  }

  private init() {
    this.add(this.lineObject)
    this.add(this.textObject)

    this.lineObject.userData.entity = this.entity
    this.lineObject.userData.hitRole = 'mleader-branch'
    this.textObject.userData.entity = this.entity
    this.textObject.userData.hitRole = 'mleader-text'
  }

  /** 更新几何体 */
  onUpdateGeometry() {
    const points = this.getRenderablePoints()
    const vertices = points.flatMap((point) => [point.x, point.y, 0])
    this.lineObject.geometry.setPositions(vertices)
    this.lineObject.geometry.attributes.position.needsUpdate = true
    this.lineObject.geometry.computeBoundingBox()
    this.lineObject.geometry.computeBoundingSphere()
    this.lineObject.computeLineDistances()

    const textPosition = this.getTextPosition()
    this.textObject.text = this.entity.contents
    this.textObject.fontSize = this.entity.textHeight
    this.textObject.rotation.z = this.entity.textRotation ?? 0
    this.textObject.position.set(textPosition.x, textPosition.y, 0)
    scheduleCadTextSync(this.textObject)
  }

  /**
   * 更新材质及文字颜色。
   *
   * @param material - 可选的外部缓存材质；未提供时根据实体样式新建
   * @param color - 可选颜色覆盖值，未提供时沿用实体自身颜色
   */
  onUpdateMaterial(material?: LineTypeMaterial, color?: number) {
    if (!this.isCacheMaterial) {
      ;(this.lineObject.material as LineTypeMaterial).dispose()
    }
    const materialColor = color ?? this.materialColor
    this.isCacheMaterial = !!material
    this.lineObject.material = material || this.createMaterial(materialColor)

    this.textObject.color = `#${materialColor.toString(16).padStart(6, '0')}`
    this.textObject.fontSize = this.entity.textHeight
    this.textObject.rotation.z = this.entity.textRotation ?? 0
    scheduleCadTextSync(this.textObject)
  }

  /** 销毁 */
  dispose() {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this.lineObject.geometry.dispose()
    if (!this.isCacheMaterial) {
      ;(this.lineObject.material as LineTypeMaterial).dispose()
    }
    disposeCadText(this.textObject)
  }

  /**
   * 克隆当前对象。
   *
   * @returns 基于同一实体新建的多重引线对象
   */
  clone() {
    return new MLeaderObject(this.entity) as any
  }

  private createLineObject(material?: LineTypeMaterial) {
    const geometry = DrawUtils.getLineGeometryByPoints(this.getRenderablePoints())
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    return createCadLine2Object(geometry, material || this.createMaterial())
  }

  private createMaterial(color?: number): LineTypeMaterial {
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    if (color !== undefined) {
      materialOption.color = color
    }
    return MaterialUtils.createMaterialByLineType(this.entity.lineType, materialOption)
  }

  private createTextObject(color?: number) {
    const textPosition = this.getTextPosition()
    const object = createCadText()
    object.text = this.entity.contents
    object.fontSize = this.entity.textHeight
    object.color = `#${(color ?? this.materialColor).toString(16).padStart(6, '0')}`
    object.position.set(textPosition.x, textPosition.y, 0)
    object.anchorX = 'left'
    object.anchorY = 'middle'
    object.rotation.z = this.entity.textRotation ?? 0
    object.whiteSpace = 'pre-wrap'
    object.overflowWrap = 'break-word'
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    object.material.depthTest = materialOption.depthTest ?? true
    object.material.depthWrite = materialOption.depthWrite ?? true
    scheduleCadTextSync(object)
    return object
  }

  private getRenderablePoints() {
    const branch = this.entity.branches[0]
    if (!branch) {
      return [
        { x: 0, y: 0 },
        { x: 0, y: 0 },
      ]
    }
    const points = getMLeaderBranchPoints(branch)
    if (points.length === 1) {
      return [points[0], points[0]]
    }
    return points
  }

  private getTextPosition() {
    if (this.entity.isTextPositionEdited) {
      return this.entity.textPosition
    }
    return getMLeaderTextPosition(
      this.entity.branches[0],
      this.entity.textPosition,
      this.entity.textHeight,
      this.entity.landingGap
    )
  }
}
